<template>
  <v-container
    id="calendar"
    class="pa-0"
  >
    <v-sheet height="64">
      <v-toolbar
        flat
        color="white"
      >
        <v-btn
          outlined
          class="ml-n4"
          color="grey darken-2"
          @click="setToday"
        >
          Today
        </v-btn>
        <v-btn
          fab
          text
          small
          color="grey darken-2"
          @click="prev"
        >
          <v-icon
            small
          >
            mdi-chevron-left
          </v-icon>
        </v-btn>
        <v-btn
          fab
          text
          small
          color="grey darken-2"
          @click="next"
        >
          <v-icon small>
            mdi-chevron-right
          </v-icon>
        </v-btn>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer />
        <v-menu
          bottom
          right
        >
          <template v-slot:activator="{ on }">
            <v-btn
              outlined
              class="ml-0"
              color="grey darken-2"
              v-on="on"
            >
              <span>{{ typeToLabel[type] }}</span>
              <v-icon right>
                mdi-menu-down
              </v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="type = 'day'">
              <v-list-item-title>Day</v-list-item-title>
            </v-list-item>
            <v-list-item @click="type = 'week'">
              <v-list-item-title>Week</v-list-item-title>
            </v-list-item>
            <v-list-item @click="type = 'month'">
              <v-list-item-title>Month</v-list-item-title>
            </v-list-item>
            <v-list-item @click="type = '4day'">
              <v-list-item-title>4 days</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>
    </v-sheet>

    <v-sheet>
      <v-toolbar
        flat
        color="white"
      >
        <v-select
          v-model="subjectFilter"
          :items="subjects"
          label="Subject"
          clearable
        />
        <v-spacer />
        <v-select
          v-model="eventTypeFilter"
          :items="eventTypes"
          label="Event Type"
          clearable
        />
      </v-toolbar>
    </v-sheet>

    <v-sheet
      flat
      color="white"
      class="cal-search-filter"
    >
      <v-row>
        <v-col
          cols="12"
          lg="4"
        >
          <v-switch
            v-model="perpetualGroups"
            label="Perpetual Groups Only"
            @change="filtersChanged"
          />
        </v-col>
        <v-col
          cols="12"
          lg="4"
        >
          <base-resource-select
            label="Benefit Admin Site"
            item-text="benefitAdminSite"
            item-value="id"
            bind-text="benefitAdminSite"
            bind-value="benefitAdminSiteId"
            :bind-item="benefitAdminSite"
            :resource="benefitAdminSiteResource"
            @change="filtersChanged"
          />
        </v-col>
        <v-col
          cols="12"
          lg="4"
        >
          <base-resource-select
            label="Upload Assigned"
            item-text="userName"
            item-value="id"
            bind-text="userName"
            bind-value="id"
            :bind-item="uploadAssigned"
            :fetch-method="fetchUploadAssigned"
            @change="filtersChanged"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          lg="4"
        >
          <base-resource-select
            label="User"
            item-text="userName"
            item-value="id"
            bind-text="userName"
            bind-value="id"
            :bind-item="user"
            :fetch-method="fetchDistinctUserList"
            @change="filtersChanged"
          />
        </v-col>
        <v-col
          v-if="userSelected()"
          cols="12"
          lg="4"
        >
          <v-switch
            v-model="isPrimary"
            label="Is Primary"
            @change="filtersChanged"
          />
        </v-col>
      </v-row>
    </v-sheet>

    <v-sheet>
      <v-toolbar
        flat
        color="white"
      >
        <v-switch
          v-if="showViewMine"
          v-model="viewMine"
          label="View Mine Only"
          @change="filtersChanged"
        />
        <v-spacer />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </v-toolbar>
    </v-sheet>

    <v-tabs
      background-color="green lighten-2"
      color="primary"
    >
      <v-tab>Calendar</v-tab>
      <v-tab>Grid</v-tab>

      <v-tab-item>
        <v-sheet height="600">
          <v-progress-linear
            v-show="loadingEvents"
            indeterminate
            color="green"
          />
          <v-calendar
            ref="calendar"
            v-model="focus"
            color="primary"
            :events="filteredEvents"
            :event-color="getEventColor"
            :now="today"
            :type="type"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="updateRange"
          />
          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            offset-x
          >
            <v-card
              color="grey lighten-4"
              min-width="350px"
              flat
            >
              <v-toolbar
                :color="selectedEvent.color"
                dark
              >
                <v-toolbar-title v-html="selectedEvent.name" />
                <v-spacer />
                <v-btn
                  v-if="eventHasAction(selectedEvent)"
                  icon
                >
                  <v-icon
                    @click="handleAction(selectedEvent)"
                  >
                    mdi-account-details
                  </v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text>
                <div>
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    For
                  </span>
                  <br class="d-flex d-sm-none">
                  <span
                    class="display-value text--secondary"
                    v-text="getEventFor(selectedEvent)"
                  />
                </div>

                <div>
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    Event Type
                  </span>
                  <br class="d-flex d-sm-none">
                  <span
                    class="display-value text--secondary"
                    v-text="getEventType(selectedEvent)"
                  />
                </div>

                <div>
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    Details
                  </span>
                  <br class="d-flex d-sm-none">
                  <span
                    class="display-value text--secondary"
                    v-text="getEventDetails(selectedEvent)"
                  />
                </div>
                <v-progress-linear
                  v-show="loadingActionDetails"
                  indeterminate
                  color="green"
                />
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  color="secondary"
                  @click="selectedOpen = false"
                >
                  <v-icon>
                    mdi-cancel
                  </v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  v-if="eventHasAction(selectedEvent)"
                  text
                  color="primary"
                  @click="handleAction(selectedEvent)"
                >
                  <v-icon>
                    mdi-account-details
                  </v-icon>
                  View
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-sheet>
      </v-tab-item>

      <v-tab-item>
        <v-data-table
          :headers="headers"
          :items="filteredEvents"
          :sort-by="['start']"
          :sort-desc="[false]"
          :expanded.sync="expanded"
          :loading="loadingEvents"
          item-key="record.id"
          show-expand
          single-expand
          multi-sort
          loading-text="Loading... Please wait"
        >
          <template v-slot:item.record.eventType="{ item }">
            <v-chip
              :color="getEventColor(item)"
              medium
            >
              <span
                class="body-1"
              >
                {{ item.record.eventType }}
              </span>
            </v-chip>
          </template>
          <template
            v-slot:expanded-item="{ headers, item }"
          >
            <td
              :colspan="headers.length"
            >
              <slot
                name="rowExpand"
              >
                <div>
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    Event Type
                  </span>
                  <br class="d-flex d-sm-none">
                  <span
                    class="display-value text--secondary"
                    v-text="getEventType(item)"
                  />
                </div>

                <div>
                  <span
                    class="display-value-label font-weight-medium text--primary"
                  >
                    Details
                  </span>
                  <br class="d-flex d-sm-none">
                  <span
                    class="display-value text--secondary"
                    v-text="getEventDetails(item)"
                  />
                </div>
                <v-progress-linear
                  v-show="loadingActionDetails"
                  indeterminate
                  color="green"
                />
                <v-btn
                  v-if="eventHasAction(item)"
                  text
                  color="primary"
                  @click="handleAction(item)"
                >
                  <v-icon>
                    mdi-account-details
                  </v-icon>
                  View
                </v-btn>
              </slot>
            </td>
          </template>
          <template v-slot:no-data>
            <v-btn
              color="primary"
              @click="loadEvents"
            >
              No Events
            </v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs>

    <open-enrollment-summary-dialog
      v-show="currentAction === 'Open Enrollment Summary'"
      :open-enrollment="selectedOpenEnrollment"
      @hide="selectedOpenEnrollment = null"
    />
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import TroodyApi from '@/services/troody/TroodyApi'
  import { isAdmin } from '@/services/roleValidator'

  export default {
    name: 'Calendar',

    components: {
      OpenEnrollmentSummaryDialog: () => import('../openEnrollments/components/OpenEnrollmentSummaryDialog'),
    },

    data: () => ({
      focus: '',
      type: 'month',
      typeToLabel: {
        month: 'Month',
        week: 'Week',
        day: 'Day',
        '4day': '4 Days',
      },
      start: null,
      end: null,
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      events: [],

      eventTypeFilter: null,
      subjectFilter: null,

      currentAction: null,
      search: '',
      expanded: [],

      headers: [
        {
          text: 'Subject',
          value: 'record.subject',
        },
        {
          text: 'Type',
          value: 'record.eventType',
        },
        {
          text: 'Date',
          value: 'start',
        },
      ],

      colorMap: [
        { eventType: 'First Pay Date', color: 'blue lighten-1' },
        { eventType: 'First Payroll Cutoff', color: 'cyan lighten-1' },
        { eventType: 'OE End', color: 'red darken-2' },
        { eventType: 'OE File Send', color: 'grey' },
        { eventType: 'OE Start', color: 'green darken-2' },
        { eventType: 'Payroll Deadline Broker', color: 'orange lighten-1' },
        { eventType: 'Payroll Deadline Group', color: 'orange darken-1' },
        { eventType: 'Plan End', color: 'red lighten-1' },
        { eventType: 'Plan Start', color: 'green ligthen-1' },
        { eventType: 'Benfit Close', color: 'amber' },
      ],

      loadingActionDetails: false,
      loadingEvents: false,
      errorMessage: null,
      displayError: false,
      formErrors: {},

      viewMine: false,

      perpetualGroups: false,
      isPrimary: false,
      benefitAdminSite: {},
      user: {},
      uploadAssigned: {},

      selectedOpenEnrollment: null,

      eventResource: new TroodyApi('event/'),
      openEnrollmentResource: new TroodyApi('openEnrollment/'),
      benefitAdminSiteResource: new TroodyApi('benefitAdminSite/'),
      identAuthUserRoleResource: new TroodyApi('identAuthUserRole/'),
    }),

    computed: {
      ...mapGetters('oidcStore', ['oidcUser']),

      showViewMine () {
        return isAdmin(this.oidcUser)
      },

      eventTypes () {
        return this.colorMap.map((item) => {
          return item.eventType
        })
      },

      subjects () {
        const events = this.events || []

        const subjects = events.map((item) => {
          return item.record.subject
        })

        const unique = subjects.filter((item, i, ar) => {
          return ar.indexOf(item) === i
        })

        return unique.sort()
      },

      filteredEvents () {
        let filtered = this.events || []

        if (filtered && filtered.length && this.eventTypeFilter) {
          filtered = filtered.filter((item) => {
            return this.eventTypeFilter.toUpperCase() === item.record.eventType.toUpperCase()
          })
        }

        if (filtered && filtered.length && this.subjectFilter) {
          filtered = filtered.filter((item) => {
            return this.subjectFilter.toUpperCase() === item.record.subject.toUpperCase()
          })
        }

        if (filtered && filtered.length) {
          if (this.search) {
            filtered = filtered.filter((item) => {
              const combined = item.name.toUpperCase() + item.start.toUpperCase() + item.record.eventType.toUpperCase()
              return combined.includes(this.search.toUpperCase())
            })
          }
        }

        return filtered
      },

      today () {
        const currentDate = new Date()
        const formattedDate = currentDate.getFullYear() + '-' + (currentDate.getMonth() + 1) + '-' + currentDate.getDate() + ' ' + currentDate.getHours() + ':' + currentDate.getMinutes() + ':' + currentDate.getSeconds()
        return formattedDate
      },

      title () {
        const { start, end } = this
        if (!start || !end) {
          return ''
        }

        const startMonth = this.monthFormatter(start)
        const endMonth = this.monthFormatter(end)
        const suffixMonth = startMonth === endMonth ? '' : endMonth

        const startYear = start.year
        const endYear = end.year
        const suffixYear = startYear === endYear ? '' : endYear

        const startDay = start.day + this.nth(start.day)
        const endDay = end.day + this.nth(end.day)

        switch (this.type) {
          case 'month':
            return `${startMonth} ${startYear}`
          case 'week':
          case '4day':
            return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`
          case 'day':
            return `${startMonth} ${startDay} ${startYear}`
        }
        return ''
      },

      monthFormatter () {
        return this.$refs.calendar.getFormatter({
          timeZone: 'UTC', month: 'long',
        })
      },
    },

    mounted () {
      if (this.$refs.calendar) {
        this.$refs.calendar.checkChange()
      }
    },

    methods: {
      viewDay ({ date }) {
        this.focus = date
        this.type = 'day'
      },

      userSelected () {
        return !!((this.user || {}).id || false)
      },

      getEventColor (event) {
        return event.color
      },

      getEventFor (event) {
        return ((event || {}).record || {}).subject || 'Unkown Broker / Group'
      },

      getEventType (event) {
        return ((event || {}).record || {}).eventType || 'Unkown Event Type'
      },

      getEventDetails (event) {
        return ((event || {}).record || {}).details || 'No further details available'
      },

      eventHasAction (event) {
        return !!(((event || {}).record || {}).actionType)
      },

      setToday () {
        this.focus = this.today
        this.type = 'day'
      },

      prev () {
        this.$refs.calendar.prev()
      },

      next () {
        this.$refs.calendar.next()
      },

      showEvent ({ nativeEvent, event }) {
        const open = () => {
          this.selectedEvent = event
          this.selectedElement = nativeEvent.target
          setTimeout(() => {
            this.selectedOpen = true
            return true
          }, 10)
        }

        if (this.selectedOpen) {
          this.selectedOpen = false
          setTimeout(open, 10)
        } else {
          open()
        }

        nativeEvent.stopPropagation()
      },

      updateRange ({ start, end }) {
        this.start = start
        this.end = end
        this.loadEvents()
      },

      nth (d) {
        return d > 3 && d < 21
          ? 'th'
          : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10]
      },

      formatDate (a, withTime) {
        return withTime
          ? `${a.getFullYear()}-${a.getMonth() + 1}-${a.getDate()} ${a.getHours()}:${a.getMinutes()}`
          : `${a.getFullYear()}-${a.getMonth() + 1}-${a.getDate()}`
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

      viewOE (event) {
        this.selectedOpenEnrollment = null
        this.loadingActionDetails = true

        if (event && event.actionId) {
          this.openEnrollmentResource.get(event.actionId)
            .then(data => {
              this.loadingActionDetails = false
              this.selectedOpenEnrollment = data
            })
            .catch((error) => {
              this.loadingActionDetails = false
              this.showError(error, 'Failed to load OE!')
            })
        }
      },

      handleAction (event) {
        const record = (event || {}).record
        if (record && record.actionType) {
          if (record.actionType === 'oesummary') {
            this.viewOE(record)
          }
        }
      },

      mapColor (event) {
        let color = 'indigo'
        if (event) {
          const matched = this.colorMap.filter((c) => {
            return c.eventType === event.eventType
          })

          if (matched && matched.length) {
            color = matched[0].color
          }
        }
        return color
      },

      transformEvents (events) {
        let transformed = null

        if (events && events.length) {
          transformed = events.map((event) => {
            return {
              name: event.subject + ' - ' + event.eventType,
              start: this.formatDate(new Date(event.eventDate), false),
              color: this.mapColor(event),
              record: event,
            }
          })
        }

        return transformed
      },

      filtersChanged () {
        this.loadEvents()
      },

      getConstraints () {
        const start = this.start.date
        const end = this.end.date
        const viewMine = this.viewMine

        const constraints = {
          start,
          end,
          viewMine,
        }

        if (this.perpetualGroups) {
          constraints.perpetualGroups = '1'
        }

        if (this.userSelected() && this.isPrimary) {
          constraints.isPrimary = this.user.id
        }

        if (this.benefitAdminSite && this.benefitAdminSite.benefitAdminSiteId) {
          constraints.eventBAS = this.benefitAdminSite.benefitAdminSiteId
        }

        if (this.user && this.user.id) {
          constraints.eventGroupRoleUser = this.user.id
        }

        if (this.uploadAssigned && this.uploadAssigned.id) {
          constraints.eventUploadAssigned = this.uploadAssigned.id
        }

        return constraints
      },

      loadEvents () {
        this.loadingEvents = true
        const constraints = this.getConstraints()
        this.eventResource.query(constraints)
          .then(data => {
            this.loadingEvents = false
            this.events = this.transformEvents(data)
          })
          .catch((error) => {
            this.loadingEvents = false
            this.showError(error, 'Failed to load events!')
          })
      },

      fetchDistinctUserList () {
        return this.identAuthUserRoleResource.getFrom('distinctUserList/')
      },

      fetchUploadAssigned () {
        return this.identAuthUserRoleResource.getFrom('distinctEmployeeList/')
      },

    },
  }
</script>

<style lang="sass">
  #calendar
    .v-calendar-weekly__day:last-child,
    .v-calendar-weekly__head-weekday:last-child
      border-right: none

    .v-calendar-weekly__week:last-child .v-calendar-weekly__day
      border-bottom: none
  .cal-search-filter
    padding-left: 31px
    padding-right: 15px
</style>
